import React, { useMemo } from 'react';
import { Pressable, ScrollView } from 'react-native';

import { useUserContext } from '../../../../common/utils/context';
import { getLocalTime } from '../../../../common/utils/time/time';
import Text from '../../../components/text/Text';
import { useThemedStyles } from '../../../hooks/useThemedStyles';

const getDuplicates = values => new Set(values.filter((value, index) => values.indexOf(value) !== index));

/**
 * Chip labels for runs: the route name (or start time), plus the start time
 * when another run has the same name, plus "Run n" (list order) when that
 * still repeats, so every chip can be told apart.
 */
export const getRunChipLabels = (runs, timezone) => {
	const times = runs.map(run => (run.planned ? getLocalTime(timezone, run.planned).format('h:mm a') : ''));
	const names = runs.map((run, index) => run.route?.name || times[index] || 'Run');
	const repeatedNames = getDuplicates(names);
	const withTimes = names.map((name, index) => (
		repeatedNames.has(name) && times[index] && name !== times[index] ? `${name} · ${times[index]}` : name
	));
	const repeatedLabels = getDuplicates(withTimes);

	return withTimes.map((label, index) => {
		if (!repeatedLabels.has(label)) {
			return label;
		}

		return label === 'Run' ? `Run ${index + 1}` : `${label} · Run ${index + 1}`;
	});
};

/**
 * RunSwitcher: one chip per run when the driver has more than one run today
 * (Home and On the run). Renders nothing for a single run.
 * Props: runs (useDriverDay().todaysRuns), activeRunId, onSelectRun(runId), style
 */
const RunSwitcher = ({ runs, activeRunId, onSelectRun, style }) => {
	const styles = useThemedStyles(createStyles);
	const { tenant } = useUserContext();
	const labels = useMemo(() => getRunChipLabels(runs || [], tenant?.timezone), [runs, tenant?.timezone]);

	if ((runs || []).length < 2 || !activeRunId) {
		return null;
	}

	return (
		<ScrollView horizontal={true} showsHorizontalScrollIndicator={false} style={[styles.scroll, style]} contentContainerStyle={styles.runSwitcher}>
			{runs.map((run, index) => {
				const isActive = run.id === activeRunId;
				const label = labels[index];
				return (
					<Pressable
						key={run.id}
						onPress={() => onSelectRun(run.id)}
						accessibilityRole="button"
						accessibilityLabel={`Run ${label}`}
						accessibilityState={{ selected: isActive }}
						style={({ pressed }) => [styles.runChip, isActive && styles.runChipActive, pressed && styles.pressed]}
					>
						<Text style={[styles.runChipText, isActive && styles.runChipTextActive]} numberOfLines={1}>{label}</Text>
					</Pressable>
				);
			})}
		</ScrollView>
	);
};

const createStyles = ({ ui: { colors, radius, space, touch, type } }) => ({
	// A horizontal ScrollView grows to fill spare height on web (short pages).
	scroll: {
		flexGrow: 0,
	},
	runSwitcher: {
		gap: space.sm,
	},
	runChip: {
		minHeight: touch.min,
		justifyContent: 'center',
		paddingHorizontal: space.lg,
		borderRadius: radius.pill,
		borderWidth: 1,
		borderColor: colors.borderStrong,
		backgroundColor: colors.card,
	},
	runChipActive: {
		backgroundColor: colors.current,
		borderColor: colors.currentBorder,
	},
	runChipText: {
		...type.bodySmallStrong,
		color: colors.textSecondary,
	},
	runChipTextActive: {
		...type.bodySmallSemibold,
		color: colors.text,
	},
	pressed: {
		opacity: 0.8,
	},
});

export default RunSwitcher;
